import {
  ARTIFACTS_PATH,
  type VideoCandidateResource,
  type VideoDeliveryResource,
  type VideoSceneResource,
} from '@studio/shared';
import { useCallback, useState, type ReactNode } from 'react';
import { RepeatFor } from '../../bulk/RepeatFor.js';
import { api, ApiError } from '../../shell/api.js';
import { EmptyState, Failure } from '../../shell/EmptyState.js';
import { Link, type Surface } from '../../shell/router.js';
import { useAsync } from '../../shell/useAsync.js';
import { clock, useVideoCandidate, useVideoShelf } from './useVideo.js';

/**
 * The video shelf and the Scene bench.
 *
 * Both are built around one claim: a Video Candidate is its ordered Scenes. The shelf
 * compares six complete storyboards by playing them, long before one has been rendered
 * in full; the bench changes a Scene or the whole story and always produces a
 * descendant. There is no timeline, no mixer and nothing to arrange frame by frame.
 */

/** Only the opened Animatic plays. There is exactly one player on the page at a time. */
function Animatic({ delivery, label }: { delivery: VideoDeliveryResource | null; label: string }): ReactNode {
  if (delivery === null || delivery.state === 'working') {
    return <p className="note">Рендерю Animatic…</p>;
  }
  if (delivery.state === 'failed' || delivery.artifactId === null) {
    return <p className="note">{delivery.message ?? 'Этот Animatic не удалось отрендерить. Scenes без изменений.'}</p>;
  }
  return (
    <video
      className="video-player"
      controls
      preload="metadata"
      aria-label={`Animatic для ${label}`}
      src={`${ARTIFACTS_PATH}/${delivery.artifactId}`}
    />
  );
}

function frameOf(candidate: VideoCandidateResource): string | null {
  return candidate.scenes.find((scene) => scene.artifactId !== null)?.artifactId ?? null;
}

/**
 * What is actually on the audio track, said the way it actually is.
 *
 * `voice over` was printed for any Candidate whose Scenes had a spoken line, and no voice
 * was ever produced: the Studio writes and times speech into the caption file and renders
 * music and effects. Saying `written voice over` is the difference between a limitation an
 * operator can plan around and a claim they would only disprove after uploading.
 */
function audioLine(candidate: VideoCandidateResource): string {
  const music = candidate.music ? 'Музыка и эффекты' : 'Без музыки';
  if (!candidate.voice) return `${music} · без голоса`;
  return candidate.voiceSynthesis.available
    ? `${music} · voice over · субтитры`
    : `${music} · written voice over, только в субтитрах · субтитры`;
}

function frameLine(candidate: VideoCandidateResource): string {
  const size = candidate.orientation === 'portrait' ? '1080×1920' : '1920×1080';
  return `${candidate.orientation === 'portrait' ? 'Портрет' : 'Альбом'} · ${size}`;
}

/**
 * The shelf: six complete storyboards, each with something to play.
 *
 * One opened Animatic at a time is the whole reason the shelf stays usable, so the
 * player is rendered for the opened Candidate and nowhere else - the others show their
 * opening frame, which is a picture and not a second thing making noise.
 */
function VideoShelf({ experimentId }: { experimentId: string }): ReactNode {
  const shelf = useVideoShelf(experimentId);
  const [open, setOpen] = useState<string | null>(null);
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const render = useCallback(
    async (candidateIds: readonly string[]) => {
      if (candidateIds.length === 0) return;
      setBusy(true);
      setError(null);
      try {
        await api.renderVideos(experimentId, candidateIds);
        shelf.refresh();
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : 'Этот рендер не удалось запустить.');
      } finally {
        setBusy(false);
      }
    },
    [experimentId, shelf],
  );

  if (shelf.error !== null) {
    return (
      <section className="page">
        <Failure message={shelf.error} onRetry={shelf.refresh} />
      </section>
    );
  }
  if (shelf.value === null) return <section className="page" aria-busy="true" />;

  const candidates = shelf.value.candidates.filter((candidate) => !candidate.hidden);

  return (
    <section className="page video-shelf">
      <div className="page-head">
        <div>
          <p className="eyebrow">Preview video · готовые раскадровки с воспроизводимыми Animatic</p>
          <h1>Выберите историю до рендера.</h1>
          <p className="note">
            Каждый Candidate — предложение Scene за Scene. Продуктовый футаж необязателен, играет только открытый Animatic.
          </p>
        </div>
        <div className="inline-actions">
          <button
            type="button"
            className="secondary"
            disabled={busy || selected.length === 0}
            onClick={() => void render(selected)}
          >
            {`Рендер выбранных (${selected.length})`}
          </button>
          <button
            type="button"
            className="primary"
            disabled={busy || candidates.length === 0}
            onClick={() => void render(candidates.map((candidate) => candidate.candidateId))}
          >
            Рендер всех
          </button>
        </div>
      </div>

      <p className="note">Keep упорядочивает эту полку. Рендер не спрашивает, был ли Candidate в Keep.</p>
      {error === null ? null : <Failure message={error} />}

      {/*
        R192: a video Target is adapted through the same `Repeat for...` as everything
        else. The destination is a separate Experiment, so on-screen copy, captions,
        voice, imagery, music, pacing and the Scenes themselves are all free to change
        there - and localized product capture stays optional.
      */}
      <RepeatFor
        label="Repeat for…"
        experimentId={experimentId}
        candidateId={open}
        note="Отдельный Experiment для нового Target. Сначала откройте Video Candidate — он уйдёт как ссылка; текст на экране, субтитры, голос, картинка, музыка, темп и Scenes адаптируются там."
      />

      {candidates.length === 0 ? (
        <EmptyState
          title="Пока нет Video Candidate."
          note="Для этого Experiment ещё ничего не сгенерировано. Generate сразу запускает шесть готовых раскадровок, у каждой — Animatic для просмотра."
          action={
            <Link to={`/experiments/${experimentId}/brief`} className="primary-link">
              К Brief
            </Link>
          }
        />
      ) : (
        <ul className="candidate-shelf" aria-label="Video Candidates">
          {candidates.map((candidate) => (
            <li key={candidate.candidateId} className="candidate-row">
              <article className="slot" aria-label={candidate.headline}>
                <div className="slot-art">
                  {open === candidate.candidateId ? (
                    <Animatic delivery={candidate.animatic} label={candidate.headline} />
                  ) : frameOf(candidate) === null ? (
                    <span className="frame frame-blank" aria-hidden="true" />
                  ) : (
                    <img
                      className="frame"
                      src={`${ARTIFACTS_PATH}/${frameOf(candidate) ?? ''}`}
                      alt={`Открывающая Scene · ${candidate.headline}`}
                      loading="lazy"
                    />
                  )}
                </div>
                <div className="slot-body">
                  <h3>{candidate.headline}</h3>
                  <p className="note">
                    {`${candidate.scenes.length} Scenes · ${clock(candidate.totalMs)} · ${frameLine(candidate)}`}
                  </p>
                  <p className="note">{audioLine(candidate)}</p>
                  <p className="note">
                    {candidate.render === null
                      ? 'Полный рендер ещё не делался.'
                      : candidate.render.state === 'working'
                        ? 'Полный рендер…'
                        : candidate.render.state === 'failed'
                          ? (candidate.render.message ?? 'Рендер остановился.')
                          : 'Отрендерено. MP4 готов к скачиванию.'}
                  </p>
                  <div className="row-actions">
                    <button
                      type="button"
                      className="ghost"
                      aria-pressed={open === candidate.candidateId}
                      onClick={() => setOpen(open === candidate.candidateId ? null : candidate.candidateId)}
                    >
                      {open === candidate.candidateId ? `Стоп ${candidate.headline}` : `Смотреть ${candidate.headline}`}
                    </button>
                    <button
                      type="button"
                      className="ghost"
                      aria-pressed={selected.includes(candidate.candidateId)}
                      onClick={() =>
                        setSelected(
                          selected.includes(candidate.candidateId)
                            ? selected.filter((one) => one !== candidate.candidateId)
                            : [...selected, candidate.candidateId],
                        )
                      }
                    >
                      {`Выбрать ${candidate.headline} для рендера`}
                    </button>
                    <button
                      type="button"
                      className="ghost"
                      disabled={busy}
                      onClick={() => void render([candidate.candidateId])}
                    >
                      {`Рендер ${candidate.headline}`}
                    </button>
                    <Link
                      to={`/experiments/${experimentId}/video/${candidate.candidateId}`}
                      className="secondary-link"
                    >
                      {`Открыть раскадровку ${candidate.headline}`}
                    </Link>
                  </div>
                </div>
              </article>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * The Scene bench: one large player, the ordered Scene ribbon, and the composer.
 *
 * Every control here that changes meaning leaves the parent untouched and produces a
 * descendant, which is why each of them navigates to the Candidate it made rather than
 * redrawing this one.
 */
function SceneBench({ experimentId, candidateId }: { experimentId: string; candidateId: string }): ReactNode {
  const open = useVideoCandidate(candidateId);
  const [selected, setSelected] = useState<number | null>(null);
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [descendantId, setDescendantId] = useState<string | null>(null);

  const candidate = open.value;

  const act = useCallback(
    async (what: string, run: () => Promise<{ kind: string; candidate?: VideoCandidateResource }>) => {
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        const result = await run();
        if (result.kind === 'descendant' && result.candidate !== undefined) {
          setDescendantId(result.candidate.candidateId);
          setNotice(`${what} создал новый Video Candidate. Этот не тронут.`);
        } else {
          setNotice(`${what} генерируется. Появится на полке как новый Candidate.`);
        }
        open.refresh();
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : `${what} не удалось отправить.`);
      } finally {
        setBusy(false);
      }
    },
    [open],
  );

  if (open.error !== null) {
    return (
      <section className="page">
        <Failure message={open.error} onRetry={open.refresh} />
      </section>
    );
  }
  if (candidate === null) return <section className="page" aria-busy="true" />;

  const scene = selected === null ? null : (candidate.scenes[selected] ?? null);

  return (
    <section className="page video-bench-page">
      <div className="page-head">
        <div>
          <p className="eyebrow">{`Video Candidate · ${candidate.headline}`}</p>
          <h1>Формируйте Scene или всю историю.</h1>
          <p className="note">
            Каждое принятое изменение создаёт готовый дочерний Candidate. Здесь ничего не правят на месте.
          </p>
        </div>
        <div className="inline-actions">
          <Link to={`/experiments/${experimentId}/video`} className="secondary-link">
            Все Animatic
          </Link>
          <button
            type="button"
            className="primary"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setError(null);
              void api
                .renderVideos(experimentId, [candidateId])
                .then(() => setNotice('Полный рендер поставлен в очередь.'))
                .catch((caught: unknown) =>
                  setError(caught instanceof ApiError ? caught.message : 'Этот рендер не удалось запустить.'),
                )
                .finally(() => {
                  setBusy(false);
                  open.refresh();
                });
            }}
          >
            Рендер этого Candidate
          </button>
        </div>
      </div>

      {error === null ? null : <Failure message={error} />}
      {notice === null ? null : (
        <p className="note" role="status">
          {notice}
        </p>
      )}
      {descendantId === null ? null : (
        <Link to={`/experiments/${experimentId}/video/${descendantId}`} className="secondary-link">
          Открыть новый Candidate
        </Link>
      )}

      <div className="video-bench">
        <div className="video-stage">
          {candidate.render !== null && candidate.render.state === 'ready' && candidate.render.artifactId !== null ? (
            <video
              className="video-player"
              controls
              preload="metadata"
              aria-label={`Полный рендер · ${candidate.headline}`}
              src={`${ARTIFACTS_PATH}/${candidate.render.artifactId}`}
            />
          ) : (
            <Animatic delivery={candidate.animatic} label={candidate.headline} />
          )}
        </div>

        <aside className="scene-panel">
          <h2>{`Лента Scenes · ${clock(candidate.totalMs)}`}</h2>
          <ol className="scene-ribbon" aria-label="Scenes по порядку">
            {candidate.scenes.map((one, index) => (
              <li key={one.id} className={selected === index ? 'scene-row selected' : 'scene-row'}>
                <button
                  type="button"
                  className="scene-select"
                  aria-pressed={selected === index}
                  onClick={() => setSelected(selected === index ? null : index)}
                >
                  {`Выбрать Scene ${index + 1} · ${one.role}`}
                </button>
                <p className="note">
                  {`${clock(one.startMs)}–${clock(one.startMs + one.durationMs)} · ${one.onScreenCopy}`}
                </p>
                <p className="note">{`Источники: ${one.sources.length === 0 ? 'не названы' : one.sources.join(', ')}`}</p>
                <div className="row-actions">
                  <button
                    type="button"
                    className="ghost"
                    disabled={busy || index === 0}
                    onClick={() =>
                      void act('Сдвиг Scene раньше', () =>
                        api.reorderScenes(candidateId, moved(candidate.scenes, index, index - 1)),
                      )
                    }
                  >
                    {`Сдвинуть Scene ${index + 1} раньше`}
                  </button>
                  <button
                    type="button"
                    className="ghost"
                    disabled={busy || index === candidate.scenes.length - 1}
                    onClick={() =>
                      void act('Сдвиг Scene позже', () =>
                        api.reorderScenes(candidateId, moved(candidate.scenes, index, index + 1)),
                      )
                    }
                  >
                    {`Сдвинуть Scene ${index + 1} позже`}
                  </button>
                </div>
              </li>
            ))}
          </ol>

          <p className="note">{`Аудио · ${audioLine(candidate)}`}</p>
          {candidate.voiceSynthesis.available ? null : (
            <p className="note limitation">{candidate.voiceSynthesis.note}</p>
          )}
          <p className="note">{`Кадр · ${frameLine(candidate)}`}</p>
          <button
            type="button"
            className="ghost"
            disabled={busy}
            onClick={() => void act('Адаптация ориентации', () => api.adaptOrientation(candidateId))}
          >
            {`Адаптировать в ${candidate.orientation === 'portrait' ? 'альбом' : 'портрет'}`}
          </button>
        </aside>

        <div className="composer bench-composer">
          <p className="composer-scope">
            {scene === null ? `Направляю ${candidate.headline}` : `Направляю Scene ${(selected ?? 0) + 1}`}
          </p>
          <label htmlFor="scene-instruction">Креативное направление</label>
          <input
            id="scene-instruction"
            value={instruction}
            placeholder="Скажите своими словами, что должно измениться"
            onChange={(event) => setInstruction(event.target.value)}
          />
          <button
            type="button"
            className="primary"
            disabled={busy || scene === null || instruction.trim() === ''}
            onClick={() => {
              const target = scene;
              if (target === null) return;
              const words = instruction.trim();
              setInstruction('');
              void act('Эта Scene', () => api.editScene(target.id, { instruction: words }));
            }}
          >
            Сгенерировать изменение
          </button>
          {scene === null ? <p className="note">Выберите Scene, чтобы направить её.</p> : <SceneCopy scene={scene} appId={candidate.appId} busy={busy} act={act} />}
        </div>
      </div>

      <CoverPreview experimentId={experimentId} candidate={candidate} />

      <Delivery candidate={candidate} experimentId={experimentId} onSaved={open.refresh} />
    </section>
  );
}

/**
 * The Candidate standing next to the cover people would actually see it beside.
 *
 * It is a reading and only a reading. The feature graphic remains a separate Asset Type
 * with an Experiment of its own, and choosing App Library material here stands it in
 * temporarily for this view - it does not change the cover, this Experiment, or what
 * any export would contain.
 */
function CoverPreview({
  experimentId,
  candidate,
}: {
  experimentId: string;
  candidate: VideoCandidateResource;
}): ReactNode {
  const [standIn, setStandIn] = useState<string | null>(null);
  const cover = useAsync(
    () => api.readVideoCoverPreview(experimentId, standIn ?? undefined),
    [experimentId, standIn],
  );

  if (cover.error !== null) return <Failure message={cover.error} onRetry={cover.reload} />;
  const value = cover.value;
  if (value === null) return <p className="note">Читаю обложку…</p>;

  return (
    <section className="video-cover-preview" aria-label="Превью обложки">
      <h2>Посмотрите под обложкой</h2>
      <p className="note">{value.note}</p>
      <div className="cover-strip">
        {value.artifactId === null ? (
          <span className="frame frame-blank" aria-hidden="true" />
        ) : (
          <img className="frame" src={`${ARTIFACTS_PATH}/${value.artifactId}`} alt={value.label} loading="lazy" />
        )}
        {frameOf(candidate) === null ? null : (
          <img
            className="frame"
            src={`${ARTIFACTS_PATH}/${frameOf(candidate) ?? ''}`}
            alt={`Открывающая Scene · ${candidate.headline}`}
            loading="lazy"
          />
        )}
      </div>
      <div className="row-actions">
        <button type="button" className="ghost" aria-pressed={standIn === null} onClick={() => setStandIn(null)}>
          Текущий Feature graphic
        </button>
        {value.options.map((option) => (
          <button
            key={option.libraryItemId}
            type="button"
            className="ghost"
            aria-pressed={standIn === option.libraryItemId}
            onClick={() => setStandIn(option.libraryItemId)}
          >
            {`Подставить ${option.label}`}
          </button>
        ))}
      </div>
    </section>
  );
}

/**
 * Correcting one Scene without buying anything.
 *
 * The words on the frame, the spoken line and the material are the three things an
 * operator can settle themselves. Each of them still makes a descendant, because each
 * of them changes what the story says - and captions follow the spoken line, which is
 * why editing speech is how captions are edited.
 */
function SceneCopy({
  scene,
  appId,
  busy,
  act,
}: {
  scene: VideoSceneResource;
  appId: string;
  busy: boolean;
  act: (what: string, run: () => Promise<{ kind: string; candidate?: VideoCandidateResource }>) => Promise<void>;
}): ReactNode {
  const [copy, setCopy] = useState(scene.onScreenCopy);
  const [speech, setSpeech] = useState(scene.speech ?? '');
  const library = useAsync(() => api.readLibrary(appId), [appId]);
  const material = (library.value?.items ?? []).filter((item) => item.artifactId !== null);

  return (
    <div className="scene-copy">
      <label htmlFor="scene-copy">Текст на экране</label>
      <input id="scene-copy" value={copy} onChange={(event) => setCopy(event.target.value)} />
      <button
        type="button"
        className="secondary"
        disabled={busy || copy === scene.onScreenCopy}
        onClick={() => void act('Перепись этой Scene', () => api.editScene(scene.id, { onScreenCopy: copy }))}
      >
        Переписать эту Scene
      </button>
      <p className="note">Смысл должен читаться без звука, поэтому эти слова — часть Scene.</p>

      <label htmlFor="scene-speech">Реплика</label>
      <input
        id="scene-speech"
        value={speech}
        placeholder="Оставьте пустым для Candidate без голоса"
        onChange={(event) => setSpeech(event.target.value)}
      />
      <button
        type="button"
        className="secondary"
        disabled={busy || speech === (scene.speech ?? '')}
        onClick={() =>
          void act('Перепись этой Scene', () =>
            api.editScene(scene.id, { speech: speech.trim() === '' ? null : speech }),
          )
        }
      >
        Сохранить реплику
      </button>
      <p className="note">Субтитры берутся из реплик — здесь и правят caption.</p>

      {material.length === 0 ? (
        <p className="note">В App Library пока нет материала с байтами — подставлять нечего.</p>
      ) : (
        <div className="row-actions">
          {material.map((item) => (
            <button
              key={item.id}
              type="button"
              className="ghost"
              disabled={busy}
              onClick={() =>
                void act('Смена материала Scene', () => api.editScene(scene.id, { materialIds: [item.id] }))
              }
            >
              {`Взять ${item.originName ?? item.role} в эту Scene`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The manual handoff.
 *
 * The MP4 and the caption file are downloadable the moment the render finishes; the
 * YouTube URL is a separate, later step whose verdict changes nothing about them.
 */
function Delivery({
  candidate,
  experimentId,
  onSaved,
}: {
  candidate: VideoCandidateResource;
  experimentId: string;
  onSaved: () => void;
}): ReactNode {
  const render = candidate.render;
  const [url, setUrl] = useState(render?.youtubeUrl ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <section className="video-delivery">
      <h2>Доставьте в Google Play вручную</h2>
      <p className="note">
        Google Play не хостит видео. Studio делает файл; вы загружаете его на YouTube и вставляете адрес обратно.
      </p>

      {render === null || render.state !== 'ready' || render.artifactId === null ? (
        <p className="note">Отрендерите этот Candidate, чтобы получить MP4 для YouTube.</p>
      ) : (
        <p className="note">
          <a href={`${ARTIFACTS_PATH}/${render.artifactId}`} download>
            Скачать MP4
          </a>
          {render.captionsArtifactId === null ? (
            ' · в этом Candidate нет речи, субтитров для загрузки нет.'
          ) : (
            <>
              {' · '}
              <a href={`${ARTIFACTS_PATH}/${render.captionsArtifactId}`} download>
                Скачать файл субтитров
              </a>
            </>
          )}
        </p>
      )}

      <ol aria-label="Чеклист загрузки на YouTube">
        {candidate.uploadChecklist.map((line) => (
          <li key={line} className="note">
            {line}
          </li>
        ))}
      </ol>

      <label htmlFor="youtube-url">YouTube URL</label>
      <input
        id="youtube-url"
        value={url}
        placeholder="https://www.youtube.com/watch?v=…"
        onChange={(event) => setUrl(event.target.value)}
      />
      <button
        type="button"
        className="secondary"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setError(null);
          void api
            .saveVideoDelivery(candidate.candidateId, { youtubeUrl: url })
            .then(onSaved)
            .catch((caught: unknown) =>
              setError(caught instanceof ApiError ? caught.message : 'Этот адрес не удалось сохранить.'),
            )
            .finally(() => setBusy(false));
        }}
      >
        Сохранить YouTube URL
      </button>
      {error === null ? null : <Failure message={error} />}

      {render === null || render.urlStatus === 'absent' ? (
        <p className="note">Адрес ещё не сохранён. MP4 готов в любом случае.</p>
      ) : render.urlStatus === 'accepted' ? (
        <p className="note" role="status">
          Это обычный URL видео на YouTube.
        </p>
      ) : (
        <p className="note" role="status">
          {`Этот адрес не подойдёт как видео листинга: ${render.urlProblems
            .map((problem) => `${problem.expected} — ${problem.observed}`)
            .join('; ')}. MP4 не затронут и по-прежнему скачивается.`}
        </p>
      )}

      {/*
       * What YouTube itself said, kept apart from the shape of the address.
       *
       * They fail independently, so they are two lines: a perfectly formed address can
       * point at a deleted video, and neither answer has ever taken anything away from
       * the MP4. A check that could not be completed says so instead of accusing the
       * address of something it was never shown to have done.
       */}
      {render === null || render.urlReachNote === null ? null : (
        <p className="note" role="status">
          {`Доступность YouTube: ${render.urlReachNote}`}
        </p>
      )}

      {candidate.verifications.map((entry) => (
        <p key={entry.code} className="note verify-in-play-console">
          {`Проверьте в текущем Play Console: ${entry.detail}`}
        </p>
      ))}

      <Link to={`/experiments/${experimentId}/export`} className="secondary-link">
        Подготовить Export Package
      </Link>
    </section>
  );
}

function moved(scenes: readonly VideoSceneResource[], from: number, to: number): readonly string[] {
  const ids = scenes.map((scene) => scene.id);
  const taken = ids[from];
  if (taken === undefined) return ids;
  const rest = ids.filter((_id, index) => index !== from);
  return [...rest.slice(0, to), taken, ...rest.slice(to)];
}

export const surfaces: readonly Surface[] = [
  {
    id: 'experiment-video-shelf',
    path: '/experiments/:experimentId/video',
    title: 'Video shelf',
    render: (params) => <VideoShelf experimentId={params.experimentId ?? ''} />,
  },
  {
    id: 'experiment-video-bench',
    path: '/experiments/:experimentId/video/:candidateId',
    title: 'Scene bench',
    render: (params) => (
      <SceneBench experimentId={params.experimentId ?? ''} candidateId={params.candidateId ?? ''} />
    ),
  },
];
